import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { saveContractPdf, type ContractPdf } from "@app/api/contract-client";
import { CopyLandingPageButton } from "@app/components/copy-landing-page-button";
import { ConfirmActionDialog } from "@app/components/confirm-action-dialog";
import {
  ACTIVATE_EVENT,
  APPROVE_EVENT_REVIEW,
  CANCEL_EVENT,
  DECLINE_EVENT_REVIEW,
  DOWNLOAD_EVENT_CONTRACT_PDF,
  GET_ADMIN_EVENT,
  GET_ADMIN_EVENT_LANDING_PAGE,
  GET_CALENDAR_BY_EVENT_ID,
  GET_EVENT_AGREEMENT,
  GET_EVENT_DASHBOARD,
  GET_EVENT_REJECTION,
} from "@app/graphql/events.queries";
import {
  GET_EVENT_AIR_CONTRACTS,
  UPDATE_EVENT_AIR_CONTRACTS,
} from "@app/graphql/policies.queries";
import { useEventBookingManifestPage } from "@app/hooks/use-event-booking-manifest-page";
import { useOrganizationId } from "@app/hooks/use-organization-id";
import {
  decodeEventDescription,
  encodeEventDescription,
} from "@app/lib/event-description";
import {
  ArrowLeftIcon,
  CalendarIcon,
  DownloadIcon,
  PinIcon,
} from "@app/lib/events-admin-icons";
import { useNotifications } from "@app/lib/ui-foundation";
import {
  createSlug,
  formatDateRange,
  formatShortDate,
} from "@app/lib/events-admin-utils";
import {
  canViewPublicEventLandingPage,
  EVENT_STATUS_DRAFT,
  EVENT_STATUS_IN_REVIEW,
  getEventStatusDisplay,
  getEventStatusToneClass,
  isDeniedEventStatus,
  normalizeEventStatus,
} from "@app/lib/event-status";
import { Skeleton } from "@app/components/skeleton";
import { Spinner } from "@app/components/spinner";
import { Link, useParams } from "react-router";
import {
  Button,
  PaginationPageDefault,
  Toggle,
} from "@its-engineering-and-development/ui-foundation/components";
import "./admin-event-detail-page.css";

interface GetEventQueryData {
  getEvent: {
    eventId: string;
    eventName: string;
    eventType: string;
    description: string;
    destination: string;
    venue: string | null;
    venueAddress: string | null;
    startDate: string;
    endDate: string;
    estimatedTravelerCount: number | null;
    status: string;
    organizerEmail: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
}

interface GetEventRejectionData {
  getEvent: {
    rejectReason: string | null;
  } | null;
}

interface GetCalendarByEventIdData {
  getCalendarByEventId: {
    organizerDisplayName: string | null;
    organizerEmail: string | null;
  } | null;
}

interface GetCalendarByEventIdVariables {
  eventId: string;
}

interface GetEventQueryVariables {
  eventId: string;
  organizationId: string;
}

type CarrierToggle = {
  carrierCode: string;
  airlineName: string;
  enabled: boolean;
  usesDefaultState: boolean;
  supportsContract: boolean;
};

type ResolvedAirline = {
  carrierCode: string;
  name: string;
  enabled: boolean;
  usesDefaultState: boolean;
  supportsContract: boolean;
};

type AirContractCodeState = {
  enabledAirlineCodes: string[];
  removedAirlineCodes: string[];
};

interface GetEventAirContractsData {
  getEventAirContracts: {
    enabledAirlineCodes: string[];
    removedAirlineCodes: string[];
    oneWorldAirContractToggles: {
      carrierToggles: CarrierToggle[];
    } | null;
  } | null;
}

interface GetEventAirContractsVariables {
  eventId: string;
  organizationId: string;
}

interface UpdateEventAirContractsData {
  updateEventAirContracts: {
    enabledAirlineCodes: string[];
    removedAirlineCodes: string[];
    oneWorldAirContractToggles: {
      carrierToggles: CarrierToggle[];
    } | null;
  };
}

interface UpdateEventAirContractsVariables {
  eventId: string;
  organizationId: string;
  enableAirlineCodes: string[];
  removeAirlineCodes: string[];
}

interface CancelEventMutationData {
  cancelEvent: {
    eventId: string;
  };
}

interface CancelEventMutationVariables {
  eventId: string;
  reason?: string;
}

interface DeclineEventReviewMutationData {
  declineEventReview: {
    eventId: string;
  };
}

interface DeclineEventReviewMutationVariables {
  eventId: string;
  reason?: string;
}

interface ActivateEventMutationData {
  activateEvent: {
    eventId: string;
  };
}

interface DownloadEventContractPdfData {
  downloadEventContractPdf: ContractPdf | null;
}

interface DownloadEventContractPdfVariables {
  eventId: string;
  organizationId: string;
}

interface GetEventAgreementData {
  getEventAgreement: {
    status: string;
    signedContractId: string | null;
    acceptedAt: string;
  } | null;
}

const AGREEMENT_DOWNLOAD_ERROR =
  "Unable to download the travel agreement. Please try again.";

interface ActivateEventMutationVariables {
  input: {
    eventId: string;
    slug: string;
    title: string;
    subtitle: string;
    description: string;
    registrationOpen: boolean;
  };
  organizationId: string;
}

interface ApproveEventReviewMutationData {
  approveEventReview: {
    eventId: string;
  };
}

interface GetEventLandingPageData {
  getEventLandingPage: {
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
  } | null;
}

interface GetEventLandingPageVariables {
  eventId: string;
}

interface GetEventDashboardData {
  getEventDashboard: {
    stats: {
      travelCounts: {
        booked: number;
      };
    };
  } | null;
}

interface GetEventDashboardVariables {
  eventId: string;
}

function extractAirlineCode(flightNumber: string | null): string {
  if (!flightNumber) return "—";
  const match = flightNumber.match(/^([A-Za-z]{2})/);
  return match ? match[1].toUpperCase() : flightNumber;
}

function formatAmountPaid(
  amount: number | null,
  currency: string | null,
): string {
  if (amount === null || amount === undefined) return "—";
  const code = currency ?? "USD";
  try {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).format(amount);
    return `${formatted} ${code}`;
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
}

function mapCarrierTogglesToRoster(
  carrierToggles: CarrierToggle[],
): ResolvedAirline[] {
  return carrierToggles
    .map((carrierToggle) => ({
      carrierCode: carrierToggle.carrierCode,
      name: carrierToggle.airlineName,
      enabled: carrierToggle.enabled,
      usesDefaultState: carrierToggle.usesDefaultState,
      supportsContract: carrierToggle.supportsContract,
    }))
    .sort((a, b) => a.carrierCode.localeCompare(b.carrierCode));
}

function normalizeCarrierCodes(codes: string[]): string[] {
  return Array.from(
    new Set(
      codes
        .map((code) => code.trim().toUpperCase())
        .filter((code) => code.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b));
}

function buildCodeStateFromArrays(
  enabledAirlineCodes: string[],
  removedAirlineCodes: string[],
): AirContractCodeState {
  const enabled = normalizeCarrierCodes(enabledAirlineCodes);
  const enabledSet = new Set(enabled);
  const removed = normalizeCarrierCodes(removedAirlineCodes).filter(
    (carrierCode) => !enabledSet.has(carrierCode),
  );

  return {
    enabledAirlineCodes: enabled,
    removedAirlineCodes: removed,
  };
}

function buildCodeStateFromCarrierToggles(
  carrierToggles: CarrierToggle[],
): AirContractCodeState {
  return buildCodeStateFromArrays(
    carrierToggles
      .filter((carrierToggle) => carrierToggle.enabled)
      .map((carrierToggle) => carrierToggle.carrierCode),
    carrierToggles
      .filter((carrierToggle) => !carrierToggle.enabled)
      .map((carrierToggle) => carrierToggle.carrierCode),
  );
}

function hasCodeState(codeState: AirContractCodeState): boolean {
  return (
    codeState.enabledAirlineCodes.length > 0 ||
    codeState.removedAirlineCodes.length > 0
  );
}

export function AdminEventDetailPage() {
  const { groupId } = useParams();
  const { showError, showSuccess } = useNotifications();
  const organizationId = useOrganizationId();
  const backTo = "/admin/dashboard";
  const resolvedEventId = groupId ?? "";

  const { data, loading } = useQuery<GetEventQueryData, GetEventQueryVariables>(
    GET_ADMIN_EVENT,
    {
      variables: {
        eventId: groupId ?? "",
        organizationId: organizationId ?? "",
      },
      skip: !groupId || !organizationId,
      fetchPolicy: "cache-and-network",
    },
  );

  const event = data?.getEvent ?? null;
  const normalizedEventStatus = normalizeEventStatus(event?.status ?? "");
  const isReviewLifecycle = normalizedEventStatus === EVENT_STATUS_IN_REVIEW;
  const isDeniedEvent = isDeniedEventStatus(event?.status ?? "");
  const shouldLoadOperationalData = Boolean(event) && !isReviewLifecycle;

  const { data: rejectionData } = useQuery<
    GetEventRejectionData,
    GetEventQueryVariables
  >(GET_EVENT_REJECTION, {
    variables: {
      eventId: groupId ?? "",
      organizationId: organizationId ?? "",
    },
    skip: !groupId || !organizationId || !isDeniedEvent,
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  });
  const denialReason = rejectionData?.getEvent?.rejectReason?.trim();

  const { data: agreementData, loading: isAgreementLoading } = useQuery<
    GetEventAgreementData,
    GetEventQueryVariables
  >(GET_EVENT_AGREEMENT, {
    variables: {
      eventId: groupId ?? "",
      organizationId: organizationId ?? "",
    },
    skip: !groupId || !organizationId || !event || isDeniedEvent,
    fetchPolicy: "cache-and-network",
  });
  const agreement = agreementData?.getEventAgreement ?? null;
  const hasSignedAgreement =
    agreement?.status === "SIGNED" && Boolean(agreement.signedContractId);

  const { data: calendarData, loading: isCalendarLoading } = useQuery<
    GetCalendarByEventIdData,
    GetCalendarByEventIdVariables
  >(GET_CALENDAR_BY_EVENT_ID, {
    variables: { eventId: groupId ?? "" },
    skip: !groupId,
    fetchPolicy: "cache-and-network",
  });

  const calendar = calendarData?.getCalendarByEventId ?? null;

  const { data: airContractsData } = useQuery<
    GetEventAirContractsData,
    GetEventAirContractsVariables
  >(GET_EVENT_AIR_CONTRACTS, {
    variables: {
      eventId: groupId ?? "",
      organizationId: organizationId ?? "",
    },
    skip: !groupId || !organizationId || !shouldLoadOperationalData,
    fetchPolicy: "cache-and-network",
  });

  const [updateEventAirContracts] = useMutation<
    UpdateEventAirContractsData,
    UpdateEventAirContractsVariables
  >(UPDATE_EVENT_AIR_CONTRACTS);
  const [downloadEventContractPdfMutation] = useMutation<
    DownloadEventContractPdfData,
    DownloadEventContractPdfVariables
  >(DOWNLOAD_EVENT_CONTRACT_PDF);

  const [cancelEventMutation, { loading: isCancellingEvent }] = useMutation<
    CancelEventMutationData,
    CancelEventMutationVariables
  >(CANCEL_EVENT);
  const [declineEventReviewMutation, { loading: isDecliningReview }] =
    useMutation<
      DeclineEventReviewMutationData,
      DeclineEventReviewMutationVariables
    >(DECLINE_EVENT_REVIEW);
  const isDecliningOrCancellingEvent = isReviewLifecycle
    ? isDecliningReview
    : isCancellingEvent;
  const [activateEventMutation, { loading: isActivatingEvent }] = useMutation<
    ActivateEventMutationData,
    ActivateEventMutationVariables
  >(ACTIVATE_EVENT);
  const [approveEventReviewMutation, { loading: isApprovingReview }] =
    useMutation<ApproveEventReviewMutationData, ActivateEventMutationVariables>(
      APPROVE_EVENT_REVIEW,
    );
  const isApprovingEvent = isReviewLifecycle
    ? isApprovingReview
    : isActivatingEvent;
  const isReviewActionPending =
    isApprovingEvent || isDecliningOrCancellingEvent;

  const { data: landingPageData } = useQuery<
    GetEventLandingPageData,
    GetEventLandingPageVariables
  >(GET_ADMIN_EVENT_LANDING_PAGE, {
    variables: { eventId: groupId ?? "" },
    skip:
      !groupId ||
      (normalizedEventStatus !== EVENT_STATUS_DRAFT && !isReviewLifecycle),
    fetchPolicy: "cache-and-network",
  });

  const MANIFEST_PAGE_SIZE = 10;

  const {
    items: passengers,
    count: passengerCount,
    page: manifestPage,
    totalPages: manifestTotalPages,
    isLoading: manifestLoading,
    setPage: setManifestPage,
  } = useEventBookingManifestPage({
    eventId: groupId,
    pageSize: MANIFEST_PAGE_SIZE,
    enabled: shouldLoadOperationalData,
  });

  const { data: dashboardData } = useQuery<
    GetEventDashboardData,
    GetEventDashboardVariables
  >(GET_EVENT_DASHBOARD, {
    variables: {
      eventId: groupId ?? "",
    },
    skip: !groupId,
    fetchPolicy: "cache-and-network",
  });

  const totalTravelersMetric = isReviewLifecycle
    ? event?.estimatedTravelerCount
    : manifestLoading && passengerCount === 0
      ? null
      : passengerCount;
  const bookedTravelersMetric =
    dashboardData?.getEventDashboard?.stats?.travelCounts?.booked ?? null;

  const [isCancelledLocally, setIsCancelledLocally] = useState(false);

  const [airlineRoster, setAirlineRoster] = useState<ResolvedAirline[]>([]);
  const [airContractCodeState, setAirContractCodeState] =
    useState<AirContractCodeState>({
      enabledAirlineCodes: [],
      removedAirlineCodes: [],
    });
  const [isAirlineUpdating, setIsAirlineUpdating] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [airlineMutationError, setAirlineMutationError] = useState<
    string | null
  >(null);
  const [cancelEventError, setCancelEventError] = useState<string | null>(null);
  const [isDownloadingAgreement, setIsDownloadingAgreement] = useState(false);

  // Derive the airline roster from the persisted GraphQL read state. Prefer the
  // named toggle block when present, but fall back to the persisted code lists
  // so a full page reload can still rebuild the roster if the upstream read
  // omits `oneWorldAirContractToggles`. We still keep the last known roster
  // when both shapes come back empty to avoid flicker on transient reads.
  useEffect(() => {
    const contracts = airContractsData?.getEventAirContracts;
    if (!contracts) {
      return;
    }

    const nextCodeState = hasCodeState(
      buildCodeStateFromArrays(
        contracts.enabledAirlineCodes ?? [],
        contracts.removedAirlineCodes ?? [],
      ),
    )
      ? buildCodeStateFromArrays(
          contracts.enabledAirlineCodes ?? [],
          contracts.removedAirlineCodes ?? [],
        )
      : buildCodeStateFromCarrierToggles(
          contracts.oneWorldAirContractToggles?.carrierToggles ?? [],
        );

    if (hasCodeState(nextCodeState)) {
      setAirContractCodeState(nextCodeState);
    }

    const rosterFromToggles = mapCarrierTogglesToRoster(
      contracts.oneWorldAirContractToggles?.carrierToggles ?? [],
    );
    if (rosterFromToggles.length > 0) {
      setAirlineRoster(rosterFromToggles);
    }
  }, [airContractsData]);

  const resolvedAirlines = airlineRoster;
  const hasLiveAirlineData = resolvedAirlines.length > 0;

  const activeAirlineCount = resolvedAirlines.filter(
    (airline) => airline.enabled,
  ).length;
  const shouldHideEventActions =
    isCancelledLocally ||
    normalizedEventStatus === "CANCELLED" ||
    normalizedEventStatus === "DENIED";
  const isPendingReview =
    normalizedEventStatus === EVENT_STATUS_DRAFT || isReviewLifecycle;
  const cancelActionCopy = isPendingReview
    ? {
        buttonClassName: "admin-event-detail-page__decline-button",
        buttonLabel: "Decline Event",
        confirmLabel: "Confirm Decline",
        loadingLabel: "Declining...",
        description: "Are you sure you want to decline this event?",
        errorDescription:
          "Unable to decline this event right now. Please try again.",
        errorTitle: "Decline failed",
        inputLabel: "Reason for decline (optional)",
        inputPlaceholder: "Enter decline reason",
        isReasonRequired: false,
        successDescription: "The event has been declined successfully.",
        successTitle: "Event declined",
        title: "Decline Event",
      }
    : {
        buttonClassName: "events-solid-button events-solid-button--danger",
        buttonLabel: "Cancel Request",
        confirmLabel: "Confirm",
        loadingLabel: "Cancelling...",
        description: "Please enter a reason before cancelling this request.",
        errorDescription:
          "Unable to cancel this request right now. Please try again.",
        errorTitle: "Cancellation failed",
        inputLabel: "Reason for cancellation",
        inputPlaceholder: "Enter cancellation reason",
        isReasonRequired: true,
        successDescription: "The request has been cancelled successfully.",
        successTitle: "Request cancelled",
        title: "Cancel Request",
      };

  const organizerDisplayName = calendar?.organizerDisplayName?.trim();
  const organizerNameDisplay =
    organizerDisplayName ||
    (isCalendarLoading && !calendar ? "Loading..." : "Not available");

  useEffect(() => {
    if (normalizedEventStatus === "CANCELLED") {
      setIsCancelledLocally(true);
    }
  }, [normalizedEventStatus]);

  if (loading && !event) {
    return (
      <section className="events-page" aria-busy="true" aria-live="polite">
        <p
          role="status"
          style={{
            border: 0,
            clip: "rect(0 0 0 0)",
            height: "1px",
            margin: "-1px",
            overflow: "hidden",
            padding: 0,
            position: "absolute",
            whiteSpace: "nowrap",
            width: "1px",
          }}
        >
          Loading group…
        </p>
        <div style={{ display: "grid", gap: "0.75rem", maxWidth: "24rem" }}>
          <Skeleton width="8rem" height="1rem" inline={false} />
          <Skeleton width="18rem" height="2.25rem" inline={false} />
          <Skeleton width="12rem" height="1.125rem" inline={false} />
        </div>
      </section>
    );
  }

  if (!event) {
    return (
      <section className="events-page">
        <div className="events-empty-state">
          <h1 className="events-page__title">Group not found</h1>
          <Link to={backTo} className="events-inline-link">
            <ArrowLeftIcon className="size-4" />
            Back to Dashboard
          </Link>
        </div>
      </section>
    );
  }

  const currentEventStatus = normalizedEventStatus ?? event.status;

  const handleAirlineToggle = async (
    airline: ResolvedAirline,
    selected: boolean,
  ) => {
    if (isAirlineUpdating) {
      return;
    }

    // Build the full enable/remove split from the whole roster with this one
    // airline flipped, so the bulk mutation always reflects every carrier's
    // current state. The mutation persists the complete air-contract
    // configuration.
    const baseCodeState = hasCodeState(airContractCodeState)
      ? airContractCodeState
      : buildCodeStateFromArrays(
          resolvedAirlines
            .filter((entry) => entry.enabled)
            .map((entry) => entry.carrierCode),
          resolvedAirlines
            .filter((entry) => !entry.enabled)
            .map((entry) => entry.carrierCode),
        );
    const nextEnabledCodes = new Set(baseCodeState.enabledAirlineCodes);
    const nextRemovedCodes = new Set(baseCodeState.removedAirlineCodes);

    if (selected) {
      nextEnabledCodes.add(airline.carrierCode);
      nextRemovedCodes.delete(airline.carrierCode);
    } else {
      nextEnabledCodes.delete(airline.carrierCode);
      nextRemovedCodes.add(airline.carrierCode);
    }

    const nextCodeState = buildCodeStateFromArrays(
      Array.from(nextEnabledCodes),
      Array.from(nextRemovedCodes),
    );
    const supportedCarrierCodes = new Set(
      resolvedAirlines
        .filter((entry) => entry.supportsContract)
        .map((entry) => entry.carrierCode),
    );
    const enableAirlineCodes = nextCodeState.enabledAirlineCodes.filter(
      (code) => supportedCarrierCodes.has(code),
    );
    const removeAirlineCodes = nextCodeState.removedAirlineCodes.filter(
      (code) => supportedCarrierCodes.has(code),
    );

    // Lock the controls and show the loader while the mutation runs. We never
    // optimistically flip local state; the UI re-renders from the mutation
    // response so the persisted server value is authoritative.
    setIsAirlineUpdating(true);
    setAirlineMutationError(null);

    try {
      const result = await updateEventAirContracts({
        variables: {
          eventId: event.eventId,
          organizationId: organizationId ?? "",
          enableAirlineCodes,
          removeAirlineCodes,
        },
      });

      const updated = result.data?.updateEventAirContracts;
      if (updated) {
        setAirContractCodeState(
          hasCodeState(
            buildCodeStateFromArrays(
              updated.enabledAirlineCodes ?? [],
              updated.removedAirlineCodes ?? [],
            ),
          )
            ? buildCodeStateFromArrays(
                updated.enabledAirlineCodes ?? [],
                updated.removedAirlineCodes ?? [],
              )
            : buildCodeStateFromCarrierToggles(
                updated.oneWorldAirContractToggles?.carrierToggles ?? [],
              ),
        );
      }
      const carrierToggles =
        updated?.oneWorldAirContractToggles?.carrierToggles ?? [];
      if (updated) {
        const roster = mapCarrierTogglesToRoster(carrierToggles);

        if (roster.length > 0) {
          setAirlineRoster(roster);
        } else {
          // The response omitted the named toggle block and provided no roster
          // membership; preserve the existing named roster and only sync the
          // enabled flags from the returned enable list.
          const enabledCodes = new Set(updated.enabledAirlineCodes ?? []);
          setAirlineRoster((prev) =>
            prev.map((entry) => ({
              ...entry,
              enabled: enabledCodes.has(entry.carrierCode),
            })),
          );
        }
      }
      setAirlineMutationError(null);
    } catch {
      setAirlineMutationError(
        "Unable to save airline authorization right now. Please try again.",
      );
    } finally {
      setIsAirlineUpdating(false);
    }
  };

  const eventRefetchOptions = {
    refetchQueries: [
      {
        query: GET_ADMIN_EVENT,
        variables: {
          eventId: event.eventId,
          organizationId: organizationId ?? "",
        },
      },
    ],
    awaitRefetchQueries: true,
  };

  const handleCancelEvent = async (reason: string) => {
    const optionalReason = reason ? { reason } : {};

    try {
      let updatedEventId: string | undefined;
      if (isReviewLifecycle) {
        const { data: mutationData } = await declineEventReviewMutation({
          variables: { eventId: event.eventId, ...optionalReason },
          ...eventRefetchOptions,
        });
        updatedEventId = mutationData?.declineEventReview.eventId;
      } else {
        const { data: mutationData } = await cancelEventMutation({
          variables: {
            eventId: event.eventId,
            ...optionalReason,
          },
          ...eventRefetchOptions,
        });
        updatedEventId = mutationData?.cancelEvent.eventId;
      }
      if (updatedEventId !== event.eventId) {
        throw new Error("Event cancellation response did not match the event");
      }

      setCancelEventError(null);
      setIsCancelledLocally(true);
      showSuccess(
        cancelActionCopy.successTitle,
        cancelActionCopy.successDescription,
      );
      setIsCancelDialogOpen(false);
    } catch {
      setCancelEventError(cancelActionCopy.errorDescription);
      showError(cancelActionCopy.errorTitle, cancelActionCopy.errorDescription);
    }
  };

  const handleApproveEvent = async () => {
    const landingPage = landingPageData?.getEventLandingPage;
    const fallbackSlug = createSlug(event.eventName) || event.eventId;

    try {
      const mutationOptions = {
        variables: {
          input: {
            eventId: event.eventId,
            slug: landingPage?.slug || fallbackSlug,
            title: landingPage?.title || event.eventName,
            subtitle: landingPage?.subtitle ?? "",
            description: encodeEventDescription(
              decodeEventDescription(
                landingPage?.description ?? event.description ?? "",
              ),
            ),
            registrationOpen: true,
          },
          organizationId: organizationId ?? "",
        },
        ...eventRefetchOptions,
      };

      let updatedEventId: string | undefined;
      if (isReviewLifecycle) {
        const { data: mutationData } =
          await approveEventReviewMutation(mutationOptions);
        updatedEventId = mutationData?.approveEventReview.eventId;
      } else {
        const { data: mutationData } =
          await activateEventMutation(mutationOptions);
        updatedEventId = mutationData?.activateEvent.eventId;
      }
      if (updatedEventId !== event.eventId) {
        throw new Error("Event approval response did not match the event");
      }

      showSuccess("Event approved", "The event is now in progress.");
    } catch {
      showError(
        "Approval failed",
        "Unable to approve this event right now. Please try again.",
      );
    }
  };

  const handleAgreementDownload = async () => {
    if (!hasSignedAgreement) {
      showError(
        "Agreement download unavailable",
        "This group does not have a signed travel agreement yet.",
      );
      return;
    }

    if (!organizationId) {
      showError(
        "Agreement download unavailable",
        "The organization is not available for this group.",
      );
      return;
    }

    setIsDownloadingAgreement(true);
    try {
      const result = await downloadEventContractPdfMutation({
        variables: {
          eventId: event.eventId,
          organizationId,
        },
      });
      const pdf = result.data?.downloadEventContractPdf;
      if (!pdf) {
        throw new Error(AGREEMENT_DOWNLOAD_ERROR);
      }

      saveContractPdf({
        contentBase64: pdf.contentBase64,
        contentType: pdf.contentType,
        filename: pdf.filename,
      });
    } catch (error) {
      showError(
        "Agreement download unavailable",
        error instanceof Error ? error.message : AGREEMENT_DOWNLOAD_ERROR,
      );
    } finally {
      setIsDownloadingAgreement(false);
    }
  };

  const agreementFilename = `Group_Travel_Agreement_${event.eventId.slice(-6)}.pdf`;

  return (
    <section className="admin-event-detail-page events-page events-page--stack events-page--with-sidebar">
      <aside className="events-detail-sidebar">
        <Link to={backTo} className="events-inline-link">
          <ArrowLeftIcon className="size-4" />
          Back to Dashboard
        </Link>

        <div className="events-detail-sidebar__summary">
          <span className="events-detail-sidebar__label">
            Group Details Summary
          </span>
          <div className="events-detail-sidebar__group-id">
            <span className="events-detail-sidebar__label">Group ID</span>
            <strong title={event.eventId}>{event.eventId}</strong>
          </div>
          <div className="events-detail-sidebar__group-id">
            <span className="events-detail-sidebar__label">Status</span>
            <span className={getEventStatusToneClass(currentEventStatus)}>
              {getEventStatusDisplay(currentEventStatus).label}
            </span>
          </div>
        </div>
      </aside>

      <div className="events-detail-main">
        <header className="events-page__hero events-page__hero--status">
          <div>
            <h1 className="events-page__title">{event.eventName}</h1>
            <div className="events-meta-row">
              <span>
                <PinIcon className="size-4" />
                {event.destination}
              </span>
              <span>
                <CalendarIcon className="size-4" />
                {formatDateRange(event.startDate, event.endDate)}
              </span>
            </div>
          </div>

          {!shouldHideEventActions ? (
            <div
              className={`events-page__hero-actions${isPendingReview ? " admin-event-detail-page__review-actions" : ""}`}
            >
              {canViewPublicEventLandingPage(currentEventStatus) ? (
                <CopyLandingPageButton
                  eventId={event.eventId || resolvedEventId}
                  className="admin-event-detail-page__copy-button"
                  iconClassName="admin-event-detail-page__copy-icon"
                />
              ) : null}
              <Button
                size="lg"
                color="primary"
                className={cancelActionCopy.buttonClassName}
                isDisabled={isReviewActionPending}
                onPress={() => {
                  setCancelEventError(null);
                  setIsCancelDialogOpen(true);
                }}
              >
                {cancelActionCopy.buttonLabel}
              </Button>
              {isPendingReview ? (
                <Button
                  size="lg"
                  color="primary"
                  className="admin-event-detail-page__approve-button"
                  onPress={() => void handleApproveEvent()}
                  isDisabled={isReviewActionPending}
                >
                  {isApprovingEvent ? "Approving..." : "Approve Event"}
                </Button>
              ) : null}
            </div>
          ) : null}
        </header>

        {!shouldHideEventActions ? (
          <ConfirmActionDialog
            isOpen={isCancelDialogOpen}
            onOpenChange={setIsCancelDialogOpen}
            title={cancelActionCopy.title}
            description={cancelActionCopy.description}
            inputLabel={cancelActionCopy.inputLabel}
            inputPlaceholder={cancelActionCopy.inputPlaceholder}
            isReasonRequired={cancelActionCopy.isReasonRequired}
            confirmLabel={cancelActionCopy.confirmLabel}
            loadingLabel={cancelActionCopy.loadingLabel}
            cancelLabel="Close"
            isLoading={isDecliningOrCancellingEvent}
            errorMessage={cancelEventError}
            onConfirm={handleCancelEvent}
          />
        ) : null}

        <div className="events-detail-grid">
          <article className="events-surface">
            <h2 className="events-surface__title">Group Information</h2>
            <dl className="events-definition-list admin-event-detail-page__group-information-list">
              <div>
                <dt>Type</dt>
                <dd title={event.eventType}>{event.eventType}</dd>
              </div>
              <div>
                <dt>Destination</dt>
                <dd title={event.destination}>{event.destination}</dd>
              </div>
              {event.organizerEmail || calendar?.organizerEmail ? (
                <div>
                  <dt>Organizer Email</dt>
                  <dd
                    title={
                      event.organizerEmail ??
                      calendar?.organizerEmail ??
                      undefined
                    }
                  >
                    {event.organizerEmail ?? calendar?.organizerEmail}
                  </dd>
                </div>
              ) : null}
              <div>
                <dt>Organizer Name</dt>
                <dd title={organizerNameDisplay}>{organizerNameDisplay}</dd>
              </div>
              {denialReason ? (
                <div>
                  <dt>Reason for denial</dt>
                  <dd title={denialReason}>{denialReason}</dd>
                </div>
              ) : null}
            </dl>
          </article>

          <article className="events-surface">
            <h2 className="events-surface__title">Key Dates</h2>
            <dl className="events-definition-list">
              <div>
                <dt>Created</dt>
                <dd>{formatShortDate(event.createdAt)}</dd>
              </div>
              <div>
                <dt>Last Modified</dt>
                <dd>{formatShortDate(event.updatedAt)}</dd>
              </div>
              <div>
                <dt>Event Start</dt>
                <dd>{formatShortDate(event.startDate)}</dd>
              </div>
              <div>
                <dt>Event End</dt>
                <dd>{formatShortDate(event.endDate)}</dd>
              </div>
            </dl>
          </article>

          <article className="events-surface events-surface--metric-card">
            <h2 className="events-surface__title">Group Metrics</h2>
            <strong className="events-surface__metric">
              {totalTravelersMetric ?? "-"}
            </strong>
            <span className="block">Total Travelers</span>
            {bookedTravelersMetric !== null &&
            !(isReviewLifecycle && bookedTravelersMetric === 0) ? (
              <span className="events-tone events-tone--info block">
                {bookedTravelersMetric} Booked
              </span>
            ) : null}
          </article>
        </div>

        {!isReviewLifecycle && hasLiveAirlineData ? (
          <article className="events-surface">
            <header className="events-page__hero events-page__hero--table">
              <h2 className="events-surface__title">
                Authorized Airlines for Booking
              </h2>
              <span className="events-table__caption">
                {isAirlineUpdating ? (
                  <span className="admin-event-detail-page__airline-saving">
                    <Spinner size={16} label="Saving airline authorization" />
                    Saving…
                  </span>
                ) : (
                  `${activeAirlineCount} active`
                )}
              </span>
            </header>
            <div className="events-airline-grid">
              {resolvedAirlines.map((airline) => (
                <div
                  key={airline.carrierCode}
                  className="events-airline-grid__item"
                >
                  <div>
                    <strong>{airline.name}</strong>
                  </div>
                  <Toggle
                    aria-label={`Authorize ${airline.name}`}
                    isSelected={airline.enabled}
                    onChange={(selected) =>
                      void handleAirlineToggle(airline, selected)
                    }
                    isDisabled={
                      isAirlineUpdating ||
                      airline.usesDefaultState ||
                      !airline.supportsContract
                    }
                    size="sm"
                    slim
                  />
                </div>
              ))}
            </div>
            {airlineMutationError ? (
              <p className="events-page__subtitle">{airlineMutationError}</p>
            ) : null}
          </article>
        ) : null}

        {!isReviewLifecycle ? (
          <article className="events-surface events-surface--table">
            <header className="events-page__hero events-page__hero--table">
              <h2 className="events-surface__title">
                Passenger List
                {passengerCount > 0 ? (
                  <span className="events-table__caption">
                    {" "}
                    ({passengerCount})
                  </span>
                ) : null}
              </h2>
            </header>

            {manifestLoading && passengers.length === 0 ? (
              <p
                className="events-page__subtitle"
                style={{ padding: "0 1.6rem 1.5rem" }}
              >
                Loading passengers…
              </p>
            ) : passengers.length === 0 ? (
              <p
                className="events-page__subtitle"
                style={{ padding: "0 1.6rem 1.5rem" }}
              >
                No passenger records found.
              </p>
            ) : (
              <>
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Airline</th>
                      <th>Airline Locator</th>
                      <th>Ticket</th>
                      <th>Amount Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passengers.map((entry) => (
                      <tr key={entry.bookingManifestId}>
                        <td>{entry.passengerName ?? "—"}</td>
                        <td>{extractAirlineCode(entry.flightNumber)}</td>
                        <td>{entry.pnr ?? "—"}</td>
                        <td>{entry.ticketNumber ?? "—"}</td>
                        <td>
                          {formatAmountPaid(entry.amountPaid, entry.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {manifestTotalPages > 1 ? (
                  <PaginationPageDefault
                    page={manifestPage}
                    total={manifestTotalPages}
                    onPageChange={setManifestPage}
                    className="admin-event-detail-page__pagination"
                  />
                ) : null}
              </>
            )}
          </article>
        ) : null}

        <article
          aria-labelledby="event-details-heading"
          className="events-surface admin-event-detail-page__event-details"
        >
          <header className="admin-event-detail-page__event-details-header">
            <h2 id="event-details-heading" className="events-surface__title">
              Event Details
            </h2>
          </header>
          <dl className="admin-event-detail-page__event-details-list">
            <div>
              <dt>Description</dt>
              <dd className="admin-event-detail-page__event-description">
                {decodeEventDescription(event.description ?? "") ||
                  "Not provided"}
              </dd>
            </div>
            <div>
              <dt>Venue</dt>
              <dd className="admin-event-detail-page__event-venue">
                {event.venue || "Not provided"}
              </dd>
              <dd className="admin-event-detail-page__event-venue-address">
                {event.venueAddress || event.destination || "Not provided"}
              </dd>
            </div>
          </dl>
        </article>

        {!isDeniedEvent ? (
          <article
            aria-labelledby="travel-agreements-heading"
            className="events-surface admin-event-detail-page__agreements"
          >
            <header className="admin-event-detail-page__agreements-header">
              <div>
                <h2
                  id="travel-agreements-heading"
                  className="events-surface__title"
                >
                  Travel Agreements
                </h2>
                <p className="admin-event-detail-page__agreements-subtitle">
                  Review and manage signed contracts for this group.
                </p>
              </div>
            </header>

            <div className="admin-event-detail-page__agreement-document">
              <span
                aria-hidden="true"
                className="admin-event-detail-page__agreement-file-icon"
              >
                PDF
              </span>
              <div className="admin-event-detail-page__agreement-file-copy">
                <strong>{agreementFilename}</strong>
                <span>
                  {hasSignedAgreement
                    ? `Accepted on ${formatShortDate(agreement.acceptedAt)}`
                    : "No signed agreement is available"}
                </span>
              </div>
              <div className="admin-event-detail-page__agreement-file-actions">
                <span className="admin-event-detail-page__agreement-status">
                  {isAgreementLoading
                    ? "Checking status..."
                    : hasSignedAgreement
                      ? "Signed & Verified"
                      : "Not signed"}
                </span>
                <button
                  type="button"
                  aria-label={`Download ${agreementFilename}`}
                  className="admin-event-detail-page__agreement-download"
                  onClick={handleAgreementDownload}
                  disabled={
                    isDownloadingAgreement ||
                    isAgreementLoading ||
                    !hasSignedAgreement
                  }
                >
                  <DownloadIcon />
                </button>
              </div>
            </div>
          </article>
        ) : null}
      </div>
    </section>
  );
}
